import { createLogger } from '@origo/logger';
import { erVegadresse } from '@origo/matrikkel-services';
import type {
    MatrikkelEnhet,
    FjernetEnhet,
    FjernetAdresse,
    Adresse,
} from '@origo/matrikkel-services';

const log = createLogger('eiendommerGruppering');

/**
 * Gruppering av eiendomslista til gate → nummer-chips for eiendomsvelgeren.
 *
 * Trukket ut av Eiendommer.tsx (task 13927) fordi grupperingen nå må holde
 * inkluderte og FJERNEDE adresser fra hverandre — en fjernet adresse blir
 * stående nedtonet i sin gate-gruppe med «+» i stedet for «×», så
 * saksbehandleren kan legge den tilbake.
 */

/** Én chip: et (gate, nummer)-par med matrikkelenhetene bak. */
export interface GateEntry {
    label: string;
    eiendommer: MatrikkelEnhet[];
    /** Sant for adresser saksbehandleren har fjernet fra utvalget. */
    fjernet: boolean;
    /**
     * Teigene enhetene lå på i den geometriske baselinen. Kun satt for fjernede
     * ENHETS-entries: deres `teigIds` er tom, så «gå til i kart» må bruke denne.
     */
    baselineTeigIds?: number[];
    /**
     * Satt når chippen er en ENKELTADRESSE på en matrikkelenhet med flere
     * vegadresser (task 13928, boligbyggerlag). Da rutes fjern/legg-tilbake til
     * adresse-sporet, og zoom bruker adressens egen koordinat.
     */
    adresse?: Adresse;
}

export interface GateGroup {
    gate: string;
    entries: GateEntry[];
    /** Antall inkluderte entries — vises i badgen. */
    antallInkludert: number;
    /** Antall fjernede entries — vises som «(n fjernet)». */
    antallFjernet: number;
}

/**
 * Samlegruppa for enheter uten gateadresse. Ikke et gatenavn, så den sorteres
 * alltid nederst i stedet for alfabetisk inn blant gatene — se
 * `groupAndSortEiendommer`.
 */
export const UTEN_GATEADRESSE = 'Matrikkelenheter uten gateadresse(r)';

/** Del «Storgata 12B» i gatenavn og nummer/bokstav. */
export function splitAdresse(adresse?: string): { gate: string; nummer: string } {
    if (!adresse) return { gate: UTEN_GATEADRESSE, nummer: '' };
    const match = adresse.match(/^(.+?)\s*([0-9]+[A-ZÆØÅa-zæøå]*)?$/);
    return {
        gate: match?.[1]?.trim() || UTEN_GATEADRESSE,
        nummer: match?.[2] || '',
    };
}

/**
 * Matrikkel-etikett for enheter uten adresse. Seksjonsnummeret tas med når det
 * finnes: uten det får to seksjoner på samme grunneiendom identisk etikett, og
 * da er det ikke mulig å se hvilken av dem «+» legger tilbake.
 */
export function getMatrikkelFallbackLabel(eiendom: MatrikkelEnhet): string {
    const anyE = eiendom as unknown as Record<string, number | undefined>;
    const gnr = anyE.gardsnummer ?? anyE.gardsnr;
    const bnr = anyE.bruksnummer ?? anyE.bruksnr;
    const snr = anyE.seksjonsnummer ?? anyE.seksjonsnr;
    const base = `${gnr}/${bnr}`;
    return snr ? `${base}/${snr}` : base;
}

/**
 * Gate og etikett for en adresse-entitet. Gata tas fra `adressenavn` — ikke ved å
 * regex-splitte `tekst` — fordi én matrikkelenhet kan ha adresser i flere gater
 * og da skal fordeles på flere gate-grupper.
 */
function adresseEtikett(adresse: Adresse): { gate: string; label: string } {
    const gate = adresse.adressenavn?.trim() || splitAdresse(adresse.tekst).gate;
    const label = adresse.adressenavn
        ? adresse.tekst.slice(adresse.adressenavn.length).trim() || adresse.tekst
        : adresse.tekst;
    return { gate, label };
}

/** Vegadressene på enheten. Matrikkeladresser er ikke andeler — se `erVegadresse`. */
const vegadresserAv = (eiendom: MatrikkelEnhet): Adresse[] =>
    (eiendom.adresser ?? []).filter(erVegadresse);

export function getEiendomDisplayLabel(eiendom: MatrikkelEnhet): { gate: string; label: string } {
    const { gate, nummer } = splitAdresse(eiendom.adresse);
    if (nummer) return { gate, label: nummer };
    // finnvedgeometri gir tom `adresse` også når enheten har ÉN vegadresse ved
    // siden av en matrikkeladresse — Furuåsen 23 i Vestby er også 86/62. Den
    // etterhentede vegadressen er da en langt bedre etikett enn gnr/bnr: uten
    // dette ville eiendommen stått som «86/62», og gateadressen vært borte.
    const vegadresser = vegadresserAv(eiendom);
    if (vegadresser.length === 1) return adresseEtikett(vegadresser[0]);
    return { gate, label: getMatrikkelFallbackLabel(eiendom) };
}

/** Stabil nøkkel for en matrikkelenhet — for delta-beregning (lagt til / fjernet). */
export function eiendomKey(e: MatrikkelEnhet): string {
    const anyE = e as unknown as Record<string, unknown>;
    return String(
        anyE.objectid
        ?? anyE.objectId
        ?? anyE.id
        ?? `${anyE.gardsnummer ?? anyE.gardsnr}/${anyE.bruksnummer ?? anyE.bruksnr}/${e.adresse ?? ''}`,
    );
}

/** Intern akkumulator: én bøtte per (gate, label, fjernet, adresseId). */
interface Bøtte {
    label: string;
    fjernet: boolean;
    eiendommer: MatrikkelEnhet[];
    baselineTeigIds: number[];
    adresse?: Adresse;
}

/**
 * En inkludert adresse-chip og en inkludert enhets-chip med samme (gate, etikett)
 * er SAMME fysiske adresse representert to ganger — én gang gjennom
 * adresse-sporet og én gang gjennom enhets-sporet. Chipene får da hver sin,
 * motstridende «×»: adresse-sporets lar matrikkelenheten stå i utvalget, mens
 * enhets-sporets trekker den ut.
 *
 * Dette LOGGES, det repareres ikke. Kollisjonen er ikke en visningsfeil — den er
 * det eneste synlige sporet av at de to sporene er uenige om hvem som eier
 * adressen, og skrivesemantikken er feil uansett hvilken chip som overlever. Å
 * kollapse dem her ville skrudd av varsellampen og latt feilen stå. Samme
 * holdning som `finnBruddrader` i arcgisAvvikRepository.
 *
 * Kjent årsak, fikset: en seksjonert grunneiendom som fikk seksjonenes adresser
 * etterhentet (se `hentAdresserForEnheterUtenAdresse`). Dukker advarselen opp
 * igjen, er det en NY vei inn i samme tilstand — f.eks. to objekter for samme
 * matrikkelenhet, der kopi B får en `adresse`-streng mens kopi A bærer hele
 * `adresser[]`.
 *
 * NB: to inkluderte ADRESSE-chips kan dele etikett uten at noe er galt — de
 * nøkles på `adresseId` nettopp fordi to andeler i prinsippet kan hete det samme
 * (se `bøtteFor`). Det er bare kollisjonen på tvers av sporene som er et brudd.
 */
function advarOmSporkollisjoner(grouped: Record<string, Record<string, Bøtte>>): void {
    for (const [gate, bøtter] of Object.entries(grouped)) {
        for (const bøtte of Object.values(bøtter)) {
            if (!bøtte.adresse || bøtte.fjernet) continue;
            if (!bøtter[`I:${bøtte.label}`]) continue;
            log.warn(
                `«${gate} ${bøtte.label}» vises både som enkeltadresse (adresseId `
                + `${bøtte.adresse.adresseId}) og som egen matrikkelenhet. De to sporene er `
                + 'uenige om hvem som eier adressen, og chipene får motstridende «×». '
                + 'Adresse-sporet skal bare gjelde USEKSJONERTE andeler.',
            );
        }
    }
}

/**
 * Grupper inkluderte og fjernede eiendommer til gate-grupper.
 *
 * Nøkkelen er `(gate, label, fjernet)` — ikke bare `(gate, label)`. En
 * grunneiendom kan være inkludert mens en av seksjonene hennes er fjernet, og
 * begge kan bære etiketten «Storgata 5»; de skal da bli TO chips, én med «×» og
 * én nedtonet med «+». Ville de kollapset til én, ville chippen hatt to
 * motstridende handlinger.
 *
 * Innenfor en gate sorteres inkluderte og fjernede hver for seg (numerisk), med
 * de inkluderte først, slik at nummerserien leses naturlig og de fjernede står
 * samlet under.
 */
export function groupAndSortEiendommer(
    inkluderte: MatrikkelEnhet[],
    fjernedeEnheter: readonly FjernetEnhet[] = [],
    fjernedeAdresser: readonly FjernetAdresse[] = [],
): GateGroup[] {
    const grouped: Record<string, Record<string, Bøtte>> = {};

    const bøtteFor = (
        gate: string,
        label: string,
        fjernet: boolean,
        adresse?: Adresse,
    ): Bøtte => {
        // Adresse-chips nøkles på adresseId, ikke etikett: to andeler kan i
        // prinsippet dele etikett, og de skal likevel være egne chips.
        const nøkkel = adresse
            ? `A${adresse.adresseId}:${fjernet ? 'F' : 'I'}`
            : `${fjernet ? 'F' : 'I'}:${label}`;
        grouped[gate] ??= {};
        return (grouped[gate][nøkkel] ??= {
            label,
            fjernet,
            eiendommer: [],
            baselineTeigIds: [],
            adresse,
        });
    };

    const leggTilEnhet = (
        eiendom: MatrikkelEnhet,
        fjernet: boolean,
        baselineTeigIds: number[] = [],
    ): void => {
        const { gate, label } = getEiendomDisplayLabel(eiendom);
        const bøtte = bøtteFor(gate, label, fjernet);
        bøtte.eiendommer.push(eiendom);
        for (const teigId of baselineTeigIds) {
            if (!bøtte.baselineTeigIds.includes(teigId)) bøtte.baselineTeigIds.push(teigId);
        }
    };

    /** En enkeltadresse (13928). */
    const leggTilAdresse = (enhet: MatrikkelEnhet, adresse: Adresse, fjernet: boolean): void => {
        const { gate, label } = adresseEtikett(adresse);
        const bøtte = bøtteFor(gate, label, fjernet, adresse);
        bøtte.eiendommer.push(enhet);
    };

    for (const eiendom of inkluderte) {
        // Bare VEGADRESSER individualiserer en andel. Har enheten flere av dem,
        // blir hver sin egen chip; ellers én chip per enhet som før. En
        // matrikkeladresse ved siden av en vegadresse er samme eiendom adressert
        // på gammelt vis, ikke en andel i tillegg — ble den regnet med her, fikk
        // Furuåsen 23 to chips med uavhengig «×» for samme fysiske eiendom.
        const vegadresser = vegadresserAv(eiendom);
        if (vegadresser.length > 1) {
            const fjernedeIds = new Set(fjernedeAdresser.map((f) => f.adresse.adresseId));
            for (const adresse of vegadresser) {
                if (fjernedeIds.has(adresse.adresseId)) continue; // kommer inn som fjernet
                leggTilAdresse(eiendom, adresse, false);
            }
        } else {
            leggTilEnhet(eiendom, false);
        }
    }

    fjernedeEnheter.forEach(({ enhet, baselineTeigIds }) => leggTilEnhet(enhet, true, baselineTeigIds));
    for (const { enhet, adresse } of fjernedeAdresser) {
        if (!erVegadresse(adresse)) {
            // Rad skrevet mens matrikkeladresser fortsatt ble chips. Den vises
            // ikke lenger, men ligger igjen i avvik-laget og kan ha effekt for
            // innbyggeren — logges så den kan ryddes.
            log.warn(
                `Fjernet-rad for matrikkeladresse ${adresse.adresseId} på matrikkelenhet `
                + `${enhet.matrikkelenhetId} vises ikke. Matrikkeladresser er ikke egne `
                + 'andeler; raden bør ryddes fra avvik-laget.',
            );
            continue;
        }
        leggTilAdresse(enhet, adresse, true);
    }

    advarOmSporkollisjoner(grouped);

    const sorterNumerisk = (a: Bøtte, b: Bøtte): number =>
        a.label.localeCompare(b.label, undefined, { numeric: true });

    return Object.entries(grouped)
        .sort(([a], [b]) => {
            // Samlegruppa er ikke en gate og skal stå nederst — alfabetisk ville
            // den havnet midt i lista, mellom «Lervikveien» og «Nordveien».
            if (a === UTEN_GATEADRESSE) return 1;
            if (b === UTEN_GATEADRESSE) return -1;
            return a.localeCompare(b);
        })
        .map(([gate, bøtter]) => {
            const alle = Object.values(bøtter);
            const inkludert = alle.filter((b) => !b.fjernet).sort(sorterNumerisk);
            const fjernet = alle.filter((b) => b.fjernet).sort(sorterNumerisk);
            return {
                gate,
                entries: [...inkludert, ...fjernet].map(
                    ({ label, eiendommer, fjernet: erFjernet, baselineTeigIds, adresse }): GateEntry => ({
                        label,
                        eiendommer,
                        fjernet: erFjernet,
                        ...(erFjernet && !adresse ? { baselineTeigIds } : {}),
                        ...(adresse ? { adresse } : {}),
                    }),
                ),
                antallInkludert: inkludert.length,
                antallFjernet: fjernet.length,
            };
        });
}

/**
 * Filtrer gruppene på gatenavn ELLER nummer/bnr. Tomt søk gir hele lista.
 * Fjernede entries filtreres på samme vilkår som inkluderte — de skal være
 * søkbare, ellers kan man ikke finne igjen adressen man vil legge tilbake.
 */
export function filterGateGroups(grupper: GateGroup[], search: string): GateGroup[] {
    const q = search.trim().toLowerCase();
    if (!q) return grupper;
    return grupper
        .map((gruppe) => {
            const gateMatch = gruppe.gate.toLowerCase().includes(q);
            const entries = gateMatch
                ? gruppe.entries
                : gruppe.entries.filter((e) => e.label.toLowerCase().includes(q));
            return {
                ...gruppe,
                entries,
                antallInkludert: entries.filter((e) => !e.fjernet).length,
                antallFjernet: entries.filter((e) => e.fjernet).length,
            };
        })
        .filter((gruppe) => gruppe.entries.length > 0);
}
